/**
 *
 * 功能：
 *  - 基于 Shadow DOM 封装自定义元素
 *  - 支持跨域 iframe 通信（postMessage）
 *  - 提供 open/close 接口以及向 iframe 发送消息的能力
 *
 * 事件：
 *  - "modal-message"：收到 iframe 消息时触发，事件 detail 包含 { action, data }
 *  - "close"：模态框关闭时触发
 */

class GeovisSSOModal extends HTMLElement {
  // 声明私有字段
  #isOpen = false;
  #iframe = null;

  constructor() {
    super();
    /** @type {ShadowRoot} */
    this.shadowRoot;
    this.attachShadow({ mode: "open" });
    this.#render();
    this.#setupEventListeners();
  }

  static get observedAttributes() {
    return ["src"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "src" && this.#iframe) {
      this.#iframe.src = newValue;
    }
  }

  // 私有方法
  #render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999; }
        .modal-content { position: absolute; background: transparent; width: 100%; height: 100%; }
        iframe { border: none; width: 100%; height: 100%; }
      </style>
      <div class="modal-content">
        <iframe></iframe>
      </div>`;
    this.#iframe = this.shadowRoot.querySelector("iframe");
  }

  #setupEventListeners() {
    window.addEventListener("message", this.#handleMessage.bind(this));
  }
  /**
   * 处理来自 iframe 的消息事件
   * @param {MessageEvent<MessageEventData>} event 消息事件对象
   */
  #handleMessage(event) {
    // 验证消息来源，确保来自当前 iframe 的同源地址
    if (!this.#iframe || event.origin !== new URL(this.#iframe.src).origin) {
      return;
    }

    const { action, data } = event.data;
    if (action === "close") this.close();
    // 触发自定义事件传递消息

    this.dispatchEvent(
      new CustomEvent("modal-message", {
        detail: { action, data },
      })
    );
  }

  open(url) {
    if (this.#iframe) {
      this.#iframe.src = url;
      this.style.display = "block";
      this.#isOpen = true;
    }
  }

  close() {
    this.style.display = "none";
    this.#isOpen = false;
    this.dispatchEvent(new CustomEvent("close"));
  }
  /**
   * 向 iframe 发送消息
   * @param {MessageEventData} message - 消息对象
   */ sendMessageToIframe(message) {
    if (this.#iframe?.contentWindow && this.#isOpen) {
      this.#iframe.contentWindow.postMessage(message, "*");
    }
  }
}

// 注册自定义元素
if (!customElements.get("geovis-sso-modal")) {
  customElements.define("geovis-sso-modal", GeovisSSOModal);
}

/**
 * 导出 createModal 方法供调用
 * 用法示例：
 *   import Modal from './modal';
 *   const modal = Modal.createModal();
 *   document.body.appendChild(modal);
 *   modal.open("https://sso.example.com/login");
 */
export default {
  createModal() {
    return document.createElement("geovis-sso-modal");
  },
};
